/*
 * "Try one of these" for the balance page.
 *
 * Two jobs: rotate through the example codes so the page shows more than one
 * balance, and copy the current one to the clipboard.
 *
 * Clicking a code fills the app widget's OWN input and submits its OWN form
 * rather than calling the balance API directly. An example that took a shortcut
 * would prove the API works while proving nothing about the thing on screen —
 * this exercises the same path a visitor's typing does, so if the widget breaks
 * the example is visibly broken too.
 *
 * <giftnote-balance> renders into an open shadow root, which is the only reason
 * this can reach the field at all. If the app ever closes it, or renames #code,
 * the button does nothing and the field stays empty — the widget itself is
 * untouched either way.
 */
(() => {
  'use strict';

  const host = document.querySelector('[data-gn-codes]');
  if (!host) return;

  const codes = (host.dataset.gnCodes || '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
  if (!codes.length) return;

  const label = host.querySelector('[data-gn-code-text]');
  const trigger = host.querySelector('[data-gn-example]');
  const copyBtn = host.querySelector('[data-gn-copy]');
  const status = host.querySelector('[data-gn-copy-status]');
  if (!label || !trigger) return;

  let i = 0;
  let timer = null;

  function render() {
    label.textContent = codes[i];
    // The visible text is uppercased in CSS; the value stays exactly as
    // authored so a case-sensitive lookup could never be broken by styling.
    trigger.setAttribute('data-gn-code', codes[i]);
  }

  function next() {
    i = (i + 1) % codes.length;
    render();
  }

  function play() {
    if (codes.length < 2) return;
    stop();
    const secs = Number(host.dataset.gnRotate) || 6;
    timer = setInterval(next, secs * 1000);
  }

  function stop() {
    clearInterval(timer);
    timer = null;
  }

  function fill(code) {
    const el = document.querySelector('giftnote-balance');
    const root = el && el.shadowRoot;
    if (!root) return false;

    const input = root.getElementById('code');
    const form = root.getElementById('form');
    if (!input || !form) return false;

    /* Assigning .value directly does not notify a framework listening for input
       events, so set through the prototype descriptor and dispatch — the same
       reason a React-controlled field ignores a plain assignment. */
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    setter.call(input, code);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    return true;
  }

  trigger.addEventListener('click', () => {
    // Stop rotating once someone has picked one: the result panel below would
    // otherwise describe a code that is no longer the one on screen.
    stop();
    fill(trigger.getAttribute('data-gn-code'));
  });

  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      const code = trigger.getAttribute('data-gn-code') || '';
      try {
        // navigator.clipboard needs a secure context; the fallback keeps the
        // button working rather than silently doing nothing.
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(code);
        } else {
          const t = document.createElement('textarea');
          t.value = code;
          t.setAttribute('readonly', '');
          t.style.position = 'fixed';
          t.style.opacity = '0';
          document.body.appendChild(t);
          t.select();
          document.execCommand('copy');
          t.remove();
        }
        host.setAttribute('data-copied', '');
        if (status) status.textContent = `Copied ${code}`;
        setTimeout(() => {
          host.removeAttribute('data-copied');
          if (status) status.textContent = '';
        }, 1600);
      } catch {
        if (status) status.textContent = 'Could not copy';
      }
    });
  }

  // Hold still while someone is reading or aiming at it.
  host.addEventListener('mouseenter', stop);
  host.addEventListener('mouseleave', play);
  host.addEventListener('focusin', stop);
  host.addEventListener('focusout', play);

  render();
  play();
})();
